import { CAPABILITY } from "../../../helpers/permissions";
import {
  applyOperationalActiveSessionOrgScope,
  applyUserIdsToQuery,
  resolveOperationalOrganizationId,
  resolveScopedUserIdsForQuery,
} from "../../../helpers/scopedQueries";
import { buildGuestExecutiveBriefingData } from "../executiveBriefingGuestDemo";

export const TIME_TRACKING_PROVIDER_ID = "timeTracking";

async function loadActiveSessions(ctx) {
  if (ctx.isGuest) {
    const demo = buildGuestExecutiveBriefingData().timeTracking;

    if (ctx.can(CAPABILITY.DASHBOARD_ACTIVE_CREW)) {
      return {
        activeCount: demo.activeCount,
        onBreakCount: demo.onBreakCount,
        selfSession: null,
      };
    }

    if (ctx.can(CAPABILITY.SESSION_CLOCK)) {
      return {
        activeCount: 0,
        onBreakCount: 0,
        selfSession: { break_active: false, status: "active" },
      };
    }

    return null;
  }

  if (ctx.isOffline || !ctx.actor?.supabase_user_id) {
    return null;
  }

  const canViewCrew = ctx.can(CAPABILITY.DASHBOARD_ACTIVE_CREW);

  if (canViewCrew) {
    const organizationId = await resolveOperationalOrganizationId(
      ctx.actor,
      ctx.supabaseClient,
      ctx.scopeContext,
    );
    const crewUserIds = await resolveScopedUserIdsForQuery(
      ctx.supabaseClient,
      ctx.actor,
      CAPABILITY.DASHBOARD_ACTIVE_CREW,
      ctx.scopeContext,
    );

    let crewQuery = ctx.supabaseClient
      .from("active_sessions")
      .select("supabase_user_id, break_active, status")
      .eq("status", "active");

    crewQuery = applyUserIdsToQuery(crewQuery, crewUserIds);
    crewQuery = applyOperationalActiveSessionOrgScope(crewQuery, organizationId);

    const { data, error } = await crewQuery;
    if (error) {
      throw error;
    }

    const sessions = data || [];
    return {
      activeCount: sessions.length,
      onBreakCount: sessions.filter((row) => row.break_active).length,
      selfSession: null,
    };
  }

  if (!ctx.can(CAPABILITY.SESSION_CLOCK)) {
    return null;
  }

  let selfSessionQuery = ctx.supabaseClient
    .from("active_sessions")
    .select("break_active, status")
    .eq("supabase_user_id", ctx.actor.supabase_user_id)
    .eq("status", "active");

  const organizationId = await resolveOperationalOrganizationId(
    ctx.actor,
    ctx.supabaseClient,
    ctx.scopeContext,
  );
  selfSessionQuery = applyOperationalActiveSessionOrgScope(
    selfSessionQuery,
    organizationId,
  );

  const { data, error } = await selfSessionQuery.maybeSingle();

  if (error) {
    throw error;
  }

  if (!data) {
    return null;
  }

  return {
    activeCount: 0,
    onBreakCount: 0,
    selfSession: data,
  };
}

export const timeTrackingProvider = {
  id: TIME_TRACKING_PROVIDER_ID,
  isEligible(ctx) {
    return (
      ctx.can(CAPABILITY.DASHBOARD_ACTIVE_CREW) ||
      ctx.can(CAPABILITY.SESSION_CLOCK)
    );
  },
  loadData: loadActiveSessions,
  buildSection(_ctx, data) {
    if (!data) {
      return null;
    }

    if (data.selfSession) {
      if (data.selfSession.break_active) {
        return "You're currently on break.";
      }
      return "You're on the clock right now.";
    }

    const { activeCount, onBreakCount } = data;

    if (activeCount <= 0) {
      return "No one is on the clock at the moment.";
    }

    const crewPhrase = pluralizeCrew(activeCount);
    if (onBreakCount <= 0) {
      return `${crewPhrase} ${activeCount === 1 ? "is" : "are"} working right now.`;
    }

    if (onBreakCount === activeCount) {
      return `${crewPhrase} ${activeCount === 1 ? "is" : "are"} on break.`;
    }

    const breakPhrase = pluralizeBreaks(onBreakCount);
    return `${crewPhrase} ${activeCount === 1 ? "is" : "are"} on the clock, and ${breakPhrase} ${onBreakCount === 1 ? "is" : "are"} on break.`;
  },
};

function pluralizeCrew(count) {
  return count === 1 ? "One crew member" : `${count} crew members`;
}

function pluralizeBreaks(count) {
  return count === 1 ? "one" : `${count}`;
}
